<template>
  <v-dialog :value="value" @input="$emit('input', $event)" content-class="velodrom-search-panel" transition="slide-y-transition" width="100%" aria-label="Search the shop">
    <section class="store-search" @keydown.esc="$emit('input', false)">
      <form :action="searchAction" method="get" class="shop-search-form" role="search" @submit="submit">
        <label for="store-search">Search</label>
        <div>
          <input ref="input" id="store-search" v-model="query" name="q" type="search" placeholder="Search products…" maxlength="200" required autocomplete="off" aria-controls="store-search-results" @keydown.down.prevent="focusResult">
          <input type="hidden" name="options[prefix]" value="last">
          <input v-if="filters.type" type="hidden" name="filter.p.product_type" :value="filters.type">
          <input v-if="filters.vendor" type="hidden" name="filter.p.vendor" :value="filters.vendor">
          <button v-if="query" type="button" class="shop-icon" aria-label="Clear search" @click="clear">↺</button>
          <button type="submit" class="shop-icon" aria-label="Submit search"><v-icon>mdi-magnify</v-icon></button>
          <button type="button" class="shop-icon" aria-label="Close search" @click="$emit('input', false)">✕</button>
        </div>
      </form>
      <div v-if="query.trim()" id="store-search-results" ref="results" class="store-search-results" :aria-busy="String(loading)" @keydown="navigate">
        <p role="status" class="store-search-status">{{ status }}</p>
        <p v-if="data.source === 'shopify' && (filters.type || filters.vendor)" class="store-search-status">Showing Shopify suggestions. Your filters will apply on the full results page.</p>
        <div v-if="!loading && !error" class="store-search-layout">
          <aside v-if="hasFacets" class="store-search-facets" aria-label="Filter search results">
            <section v-for="group in facetGroups" :key="group.key">
              <h2>{{ group.title }}</h2>
              <label v-for="facet in (data.facets[group.list] || []).slice(0,15)" :key="facet.name">
                <input type="checkbox" :checked="filters[group.key] === facet.name" @change="toggleFilter(group.key, facet.name)">
                <span>{{ facet.name }}</span><small>{{ facet.count }}</small>
              </label>
            </section>
          </aside>
          <div class="store-search-main">
            <div v-if="filters.type || filters.vendor || data.size_filter" class="store-search-chips">
              <button v-for="group in facetGroups.filter(g => filters[g.key])" :key="group.key" type="button" @click="toggleFilter(group.key, filters[group.key])">{{ filters[group.key] }} ×</button>
              <button v-if="data.size_filter" type="button" @click="removeSize">Size {{ data.size_filter }} ×</button>
            </div>
            <template v-if="data.collections && data.collections.length">
              <h2>Collections</h2>
              <div class="store-search-collections">
                <a v-for="collection in data.collections" :key="collection.url" :href="shopUrl(collection.url)">
                  <img v-if="collection.image || collection.featured_image" :src="collection.image || collection.featured_image.url" alt="" loading="lazy">
                  <span>{{ collection.title }}<small v-if="collection.productsCount != null">{{ collection.productsCount }} products</small></span>
                </a>
              </div>
            </template>
            <div v-if="data.queries && data.queries.length" class="store-search-chips" aria-label="Suggestions">
              <a v-for="item in data.queries" :key="item.url" :href="shopUrl(item.url)">{{ item.text }}</a>
            </div>
            <h2 v-if="data.results.length">Products</h2>
            <div class="store-search-grid">
              <article v-for="product in data.results" :key="product.id" class="store-search-card">
                <a :href="shopUrl(product.url)">
                  <div class="store-search-media">
                    <img v-if="product.image" :src="product.image" :alt="product.title" loading="lazy">
                    <span v-if="Number(product.compare_at_price) > Number(product.price)" class="store-search-badge">-{{ Math.round((1 - Number(product.price) / Number(product.compare_at_price)) * 100) }}%</span>
                  </div>
                  <small>{{ product.vendor }}</small>
                  <div>{{ product.title }}{{ product.price_varies && product.price_variant_title ? ' - ' + product.price_variant_title : '' }}</div>
                  <p class="store-search-price">{{ product.price_varies && !product.price_variant_title ? 'From ' : '' }}{{ money(product.price) }} <s v-if="Number(product.compare_at_price) > Number(product.price)">{{ money(product.compare_at_price) }}</s></p>
                </a>
                <div v-if="product.colors && product.colors.length" class="store-search-swatches">
                  <a v-for="color in product.colors.slice(0,5)" :key="color.url" :href="shopUrl(color.url)" :aria-label="color.name || color.title" :title="color.name || color.title" :style="swatch(color)" :class="{selected: color.selected}"></a>
                  <span v-if="product.colors.length > 5">+{{ product.colors.length - 5 }}</span>
                </div>
                <small :class="{'store-search-unavailable': !product.available}">{{ stock(product) }}</small>
              </article>
            </div>
          </div>
        </div>
        <a v-if="!loading" class="store-search-all" :href="allResultsUrl">{{ data.total != null && !error ? 'See all ' + data.total + ' results' : 'Search the shop for “' + query.trim() + '”' }} →</a>
      </div>
    </section>
  </v-dialog>
</template>
<script>
import axios from 'axios';
const empty = () => ({ results: [], collections: [], facets: {}, total: null });
export default {
  props: { value: Boolean, language: { type: String, default: 'en' } },
  data: () => ({ query: '', data: empty(), loading: false, error: false, timer: null, sequence: 0, retries: 0, cancel: null, filters: { type: null, vendor: null }, facetGroups: [{ key: 'type', list: 'types', title: 'Category' }, { key: 'vendor', list: 'vendors', title: 'Brand' }] }),
  computed: {
    searchAction() { return `https://www.velodrom.cc${this.language === 'en' ? '' : '/' + this.language}/search`; },
    allResultsUrl() {
      const params = new URLSearchParams({ q: this.query.trim(), 'options[prefix]': 'last' });
      if (this.filters.type) params.set('filter.p.product_type', this.filters.type);
      if (this.filters.vendor) params.set('filter.p.vendor', this.filters.vendor);
      return this.searchAction + '?' + params;
    },
    hasFacets() { return this.facetGroups.some(group => (this.data.facets[group.list] || []).length); },
    status() {
      if (this.loading) return 'Searching…';
      if (this.error) return 'Live search is temporarily unavailable. You can still search the shop below.';
      if (this.data.resolution_pending) return this.retries < 3 ? 'Looking for similar products…' : 'No instant matches. Search the shop below for more results.';
      if (!this.data.results.length) return `No products found for “${this.query.trim()}”`;
      return `${this.data.total == null ? this.data.results.length + ' suggested' : this.data.total} results for “${this.query.trim()}”`;
    }
  },
  watch: {
    value(open) { if (open) { this.$nextTick(() => requestAnimationFrame(() => { if (this.value && this.$refs.input) this.$refs.input.focus(); })); if (this.query.trim()) this.schedule(); } else this.stop(); },
    query() { this.filters = { type: null, vendor: null }; this.schedule(); },
    language() { if (this.value && this.query.trim()) this.schedule(); }
  },
  beforeDestroy() { this.stop(); },
  methods: {
    stop() { clearTimeout(this.timer); this.sequence++; if (this.cancel) this.cancel.cancel(); this.cancel = null; this.loading = false; },
    schedule() { this.stop(); this.retries = 0; this.data = empty(); this.error = false; if (!this.query.trim() || !this.value) return; this.loading = true; this.timer = setTimeout(this.search, 150); },
    async search() {
      const sequence = this.sequence;
      this.cancel = axios.CancelToken.source();
      try {
        const { data } = await axios.get('/api/shop-search', { params: { q: this.query.trim(), lang: this.language, ...this.filters }, cancelToken: this.cancel.token, timeout: 12000 });
        if (sequence !== this.sequence) return;
        this.data = { ...empty(), ...data };
        if (data.resolution_pending && this.retries < 3) { this.retries++; this.timer = setTimeout(() => { if (sequence === this.sequence && this.value) this.search(); }, 1500); }
      } catch (error) { if (sequence === this.sequence && !axios.isCancel(error)) this.error = true; }
      finally { if (sequence === this.sequence) this.loading = false; }
    },
    toggleFilter(key, name) { this.filters[key] = this.filters[key] === name ? null : name; this.schedule(); },
    removeSize() { const size = String(this.data.size_filter).toLowerCase(); this.query = this.query.split(/\s+/).filter(word => word.toLowerCase() !== size).join(' '); },
    clear() { this.query = ''; this.$refs.input.focus(); },
    submit(event) { if (!this.query.trim()) event.preventDefault(); },
    focusResult() { const first = this.$refs.results && this.$refs.results.querySelector('a, button, input'); if (first) first.focus(); },
    navigate(event) {
      if (!['ArrowDown', 'ArrowUp'].includes(event.key) || event.altKey || event.ctrlKey || event.metaKey) return;
      const items = Array.from(this.$refs.results.querySelectorAll('a, button, input'));
      const index = items.indexOf(document.activeElement);
      if (index < 0) return;
      event.preventDefault();
      const next = items[index + (event.key === 'ArrowDown' ? 1 : -1)];
      (next || this.$refs.input).focus();
    },
    shopUrl(path) { try { const url = new URL(path, 'https://www.velodrom.cc'); return url.protocol === 'https:' && ['www.velodrom.cc', 'velodrom.cc'].includes(url.hostname) ? url.href : this.allResultsUrl; } catch (_) { return this.allResultsUrl; } },
    money(value) { return new Intl.NumberFormat(this.language, { style: 'currency', currency: 'EUR' }).format(Number(value) || 0); },
    stock(product) {
      if (!product.available) return 'Out of stock';
      const sizes = product.available_sizes || [];
      if (product.price_varies || !sizes.length || !product.total_variants) return '✓ In stock';
      if (sizes.length >= 6) return sizes.length === product.total_variants ? '✓ All sizes available' : '✓ Various sizes available';
      const names = { 'XX-SMALL': 'XXS', 'X-SMALL': 'XS', SMALL: 'S', MEDIUM: 'M', LARGE: 'L', 'X-LARGE': 'XL', 'XX-LARGE': '2XL' };
      return '✓ ' + sizes.map(size => names[size] || size).join(' · ');
    },
    swatch(color) { const valid = value => /^#[\da-f]{3,8}$/i.test(value || ''); return { background: valid(color.hex) ? valid(color.hex2) ? `linear-gradient(135deg, ${color.hex} 50%, ${color.hex2} 50%)` : color.hex : '#eee' }; }
  }
};
</script>
<style>
.store-search { color:#171917; font-family:'Eduard Breuninger',sans-serif; }
.store-search .shop-search-form { position:sticky; top:0; z-index:2; background:white; }
.store-search-results { border-top:1px solid #eee; padding:20px 30px 30px; }
.store-search-status { font-size:14px; margin-bottom:20px; }
.store-search-layout { display:flex; gap:30px; }
.store-search h2 { font-size:12px; font-weight:500; text-transform:uppercase; letter-spacing:.08em; margin:0 0 14px; }
.store-search-facets { width:190px; flex-shrink:0; }
.store-search-facets section { margin-bottom:28px; }
.store-search-facets label { display:flex; gap:8px; align-items:center; font-size:12px; padding:6px 0; cursor:pointer; }
.store-search-facets label span { flex:1; }
.store-search-facets input { accent-color:#171917; }
.store-search-main { flex:1; min-width:0; }
.store-search a { color:inherit !important; text-decoration:none; }
.store-search-collections { display:flex; gap:12px; overflow:auto; margin-bottom:24px; padding-bottom:6px; }
.store-search-collections a { display:flex; align-items:center; gap:12px; border:1px solid #e6e6e6; padding:8px; min-width:210px; max-width:240px; font-size:13px; flex-shrink:0; }
.store-search-collections img { width:55px; height:55px; object-fit:contain; }
.store-search-collections small { display:block; color:#777; }
.store-search-grid { display:grid; grid-template-columns:repeat(6,minmax(0,1fr)); gap:24px 16px; }
.store-search-card { font-size:13px; line-height:1.45; }
.store-search-media { position:relative; aspect-ratio:1; background:#f7f7f7; margin-bottom:10px; }
.store-search-media img { width:100%; height:100%; object-fit:contain; }
.store-search-badge { position:absolute; top:8px; left:8px; background:#171917; color:white; padding:3px 7px; font-size:11px; }
.store-search-card small { display:block; color:#61715e; font-size:11px; margin:5px 0; }
.store-search-card a > small { color:#777; text-transform:uppercase; }
.store-search-card .store-search-unavailable { color:#888; }
.store-search-card .store-search-price { margin:8px 0; }
.store-search-price s { color:#888; margin-left:6px; font-size:11px; }
.store-search-swatches { display:flex; align-items:center; gap:6px; margin:8px 0; }
.store-search-swatches a { width:16px; height:16px; border:1px solid #ccc; }
.store-search-swatches .selected { outline:1px solid #333; outline-offset:2px; }
.store-search-chips { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:20px; }
.store-search-chips button, .store-search-chips a { border:1px solid #ddd; padding:6px 12px; font-size:12px; }
.store-search-all { display:block; border-top:1px solid #ddd; margin-top:24px; padding:18px 0 0; text-align:center; font-size:14px; }
.store-search a:hover { text-decoration:underline; }
.store-search :focus-visible { outline:2px solid #344b35; outline-offset:3px; }
.store-search .shop-search-form input[type=hidden] { display:none; }
@media(max-width:1200px) { .store-search-grid { grid-template-columns:repeat(4,minmax(0,1fr)); } }
@media(max-width:749px) { .store-search-results { padding:16px; } .store-search-layout { flex-direction:column; gap:10px; } .store-search-facets { display:flex; gap:20px; width:100%; max-height:150px; overflow:auto; } .store-search-facets section { min-width:160px; } .store-search-grid { grid-template-columns:repeat(2,minmax(0,1fr)); gap:20px 12px; } .store-search .shop-search-form { padding:22px 16px; } }
</style>
